import type { RaceEventMode, RacerSnapshot } from '../../shared/types.js';
import { eventResultLabel } from '../../shared/race-events.js';
import type { GhostStatus } from '../game/lap-ghost-store.js';
import { formatNumber, t } from '../i18n/index.js';

export function ordinal(value: number): string {
  const suffix = value % 100 >= 11 && value % 100 <= 13 ? 'TH' : value % 10 === 1 ? 'ST' : value % 10 === 2 ? 'ND' : value % 10 === 3 ? 'RD' : 'TH';
  // German ordinals are "1." — the suffix key carries the whole pattern.
  return t(`{n}${suffix}`, { n: value });
}

export function heatRunDisplayScore(racer: RacerSnapshot): number {
  const bankableHeat = Math.max(0, racer.completedLaps);
  return Math.round(racer.styleScore + bankableHeat * bankableHeat * 1_500 + racer.health * 4);
}

/**
 * Classification cell for a results row. Races decided on the line show the
 * gap to the winner, the way broadcast timing screens do; score-based modes
 * keep their label.
 */
export function resultStatus(mode: RaceEventMode, racer: RacerSnapshot, winnerFinishedAt: number | null): string {
  const label = eventResultLabel(mode, racer.place, racer.finishedAt);
  if (label !== 'FINISHED' || racer.finishedAt === null || winnerFinishedAt === null) {
    return `<em data-result="${label.toLowerCase()}">${t(label)}</em>`;
  }
  if (racer.place === 1) return `<em data-result="winner">${t('WINNER')}</em>`;
  return `<em data-result="gap">+${formatGap(racer.finishedAt - winnerFinishedAt)}</em>`;
}

export function formatGap(milliseconds: number): string {
  const safe = Math.max(0, Math.round(milliseconds));
  if (safe < 60_000) return formatNumber(safe / 1_000, { minimumFractionDigits: 3, maximumFractionDigits: 3, useGrouping: false });
  return formatLapTime(safe);
}

export function formatLapTime(milliseconds: number): string {
  const safe = Math.max(0, Math.round(milliseconds));
  const minutes = Math.floor(safe / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1_000);
  const millis = safe % 1_000;
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

export function formatEventClock(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatSectorTime(milliseconds: number): string {
  return formatNumber(Math.max(0, milliseconds) / 1_000, { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });
}

export function medalLabel(lapTimeMs: number | null, targets: GhostStatus['targets']): 'GOLD' | 'SILVER' | 'BRONZE' | 'NO MEDAL' {
  if (lapTimeMs === null || lapTimeMs <= 0) return 'NO MEDAL';
  if (lapTimeMs <= targets.goldMs) return 'GOLD';
  if (lapTimeMs <= targets.silverMs) return 'SILVER';
  if (lapTimeMs <= targets.bronzeMs) return 'BRONZE';
  return 'NO MEDAL';
}

/**
 * Rows for the live standings tower. Six rows fit; when the player runs
 * outside them, keep the leaders on top and show the player's own battle
 * (one ahead, one behind) below a break, the way broadcast towers do.
 * `null` marks the break.
 */
export function standingsWindow<T extends { id: string; place: number }>(
  racers: readonly T[],
  playerId: string | null | undefined,
  rows = 6,
): (T | null)[] {
  const ordered = [...racers].sort((a, b) => a.place - b.place);
  const playerIndex = playerId ? ordered.findIndex((racer) => racer.id === playerId) : -1;
  if (ordered.length <= rows || playerIndex < rows) return ordered.slice(0, rows);
  const battleEnd = Math.min(ordered.length, playerIndex + 2);
  const battleStart = Math.max(0, battleEnd - 3);
  const leaders = rows - 1 - (battleEnd - battleStart);
  return [...ordered.slice(0, leaders), null, ...ordered.slice(battleStart, battleEnd)];
}
